// pages/ltv/ltvUi.jsx — shared display atoms for the /ltv page (SOUND-552, SOUND-811).
//
// Shared by the current-method modeler and the decomposed (v2) view so both
// render with the same colours, panels and inputs. SOUND-837 moved the page
// onto the CRM's brand system: every colour is a theme token (tailwind.config.js
// addBase), cards are the Risk page's `surface rounded-xl ring-1 ring-border`,
// tables use the Risk page's header/row classes, and KPI tiles are
// components/PageKpis.
import React from "react";
import clsx from "clsx";
import { Database, SlidersHorizontal, Info } from "lucide-react";

export const isDark = () => typeof document !== "undefined" && document.documentElement.classList.contains("dark");
export function cssVar(name, alpha = 1) {
  if (typeof window === "undefined") return "";
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return "";
  const [h, s, l] = raw.split(/\s+/);
  return `hsla(${parseFloat(h)}, ${s}, ${l}, ${alpha})`;
}

// ─── chart colours, by ROLE, resolved from theme tokens ───
// primary     the main series (measured / realized / contribution): brand
//             green --brand-600, the same green as ReportChart's PALETTE[0].
// secondary   the second series (projected): --info, the token for
//             ReportChart's PALETTE[1] blue.
// warning     CAC reference lines, "payment failed".
// success / destructive   positive / negative deltas and outcomes.
// primaryText brand green legible as SVG TEXT: --brand-700 on light (the
//             token for text on white), --brand on dark.
const INK = {
  primary: "--brand-600",
  secondary: "--info",
  warning: "--warning",
  success: "--success",
  destructive: "--destructive",
};
export const ink = (role, a = 1) =>
  role === "primaryText"
    ? cssVar(isDark() ? "--brand" : "--brand-700", a)
    : cssVar(INK[role], a);

// Brand green as an HTML text class (same rule as primaryText).
export const BRAND_TEXT = "text-brand-700 dark:text-brand";

export const tooltipStyle = () => ({
  background: cssVar("--card", 0.98), border: `1px solid ${cssVar("--border", 1)}`,
  borderRadius: 8, fontSize: 12,
});

// ─── formatting ───
export const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(n || 0));
export const fmtMoney2 = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n || 0));
export const fmtPct1 = (n) => (n === null || n === undefined ? "—" : `${(Number(n) * 100).toFixed(1)}%`);
export const fmtNum = (n, d = 1) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: d }));

// ─── CRM chrome (matches pages/risk/RiskView.jsx) ───
export const CARD = "surface rounded-xl ring-1 ring-border";
// Field / stat label — the CRM's form-label style (RiskView filter bar).
export const EYEBROW = "text-xs text-muted-foreground";
// Table header cell + body cell, as on the Risk MID tables.
export const TH = "px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider";
export const TD = "px-4 py-2.5";
export const TR = "transition-colors hover:bg-muted/40";
// Segmented control (ReportChart's Seg): container + per-option classes.
export const SEG = "inline-flex rounded-lg ring-1 ring-border overflow-hidden";
export const segOption = (active) => clsx("transition-colors disabled:text-muted-foreground/40 disabled:cursor-not-allowed",
  active ? "bg-brand/15 text-foreground" : "text-muted-foreground hover:bg-muted/60");

// Token chips, in the style of the PageKpis tones (tinted bg + tone text + ring).
const CHIP = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1";
export const MeasuredBadge = ({ className }) => (
  <span className={clsx(CHIP, "bg-brand/10 ring-brand/30", BRAND_TEXT, className)}>
    <Database className="h-3 w-3" /> Measured
  </span>
);
export const AssumptionBadge = ({ className }) => (
  <span className={clsx(CHIP, "bg-info/10 text-info ring-info/30", className)}>
    <SlidersHorizontal className="h-3 w-3" /> Assumption
  </span>
);

const PILL_TONES = {
  up: "bg-success/10 text-success",
  down: "bg-destructive/10 text-destructive",
  warn: "bg-warning/10 text-warning",
  muted: "bg-muted text-muted-foreground",
};
export function Pill({ tone = "muted", children }) {
  return (
    <span className={clsx("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", PILL_TONES[tone])}>
      {children}
    </span>
  );
}

// ─── panel (title + question subtitle + legend swatches) ───
export function Panel({ title, question, legend, wide, children }) {
  return (
    <section className={clsx(CARD, "p-5 flex flex-col min-w-0", wide && "lg:col-span-2")}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          {question && <p className="mt-0.5 text-xs text-muted-foreground max-w-[58ch]">{question}</p>}
        </div>
        {legend && <div className="flex items-center gap-3.5 flex-wrap text-[11.5px] text-muted-foreground">{legend}</div>}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}
export const Swatch = ({ color, dash }) => (
  dash
    ? <span className="inline-block align-middle mr-1.5" style={{ width: 16, borderTop: `2px dashed ${color}` }} />
    : <span className="inline-block align-middle mr-1.5 rounded-[2px]" style={{ width: 11, height: 11, background: color }} />
);
export const Key = ({ color, dash, children }) => (
  <span className="inline-flex items-center"><Swatch color={color} dash={dash} />{children}</span>
);

// ─── assumption input field ───
export function NumberField({ label, value, onChange, prefix, suffix, step = 1, min, placeholder, tooltip }) {
  return (
    <div className="min-w-0">
      <label className={clsx(EYEBROW, "mb-1 flex items-center gap-1")}>
        {label}
        {tooltip && <span className="text-muted-foreground/70 cursor-help" title={tooltip}><Info className="h-3 w-3" /></span>}
      </label>
      <div className="flex items-center rounded-md border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring/50">
        {prefix && <span className="pl-2.5 pr-1 text-sm text-muted-foreground select-none">{prefix}</span>}
        <input type="number" inputMode="decimal" step={step} min={min} value={value} placeholder={placeholder}
          className="w-full bg-transparent px-2.5 py-2 text-sm text-foreground outline-none tabular-nums"
          onChange={(e) => onChange(e.target.value)} />
        {suffix && <span className="pr-2.5 pl-1 text-sm text-muted-foreground select-none">{suffix}</span>}
      </div>
    </div>
  );
}
export function SelectField({ label, value, onChange, children }) {
  return (
    <div className="min-w-0">
      <label className={clsx(EYEBROW, "block mb-1")}>{label}</label>
      <select className="select w-full" value={value} onChange={(e) => onChange(e.target.value)}>{children}</select>
    </div>
  );
}

